import React, {
  createContext,
  ReactNode,
  useMemo,
  useContext,
  useCallback,
} from 'react';

// Types
import { AuthType, AvailableTenant } from '../types/auth';

import { useSessionStorage } from './useSessionStorage';

export interface AuthContextType {
  user: AuthType;
  setUser: (data: AuthType | null) => void;
  activeTenantId: string | null;
  availableTenants: AvailableTenant[];
  setActiveTenantId: (tenantId: string) => void;
  /**
   * Current active role of the user
   * Extracted from user.user.rolActive for convenience
   */
  currentRole: string | null;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useSessionStorage('user');

  // Función auxiliar para actualizar solo el activeTenantId
  const setActiveTenantId = useCallback(
    (tenantId: string) => {
      if (user) {
        setUser({
          ...user,
          user: {
            ...user.user,
            activeTenantId: tenantId,
          },
        });

        // Guardar explícitamente en sessionStorage para acceso rápido
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('activeTenantId', tenantId);
        }
      }
    },
    [user, setUser]
  );

  const value = useMemo(
    () => ({
      user,
      setUser,
      // Siempre usar user.user.activeTenantId como fuente única de verdad
      activeTenantId: user?.user?.activeTenantId || null,
      // Siempre usar user.availableTenants (desde AuthType root) como fuente única
      availableTenants: user?.availableTenants || [],
      setActiveTenantId,
      // Current active role from user
      currentRole: user?.user?.rolActive || null,
    }),
    [user, setUser, setActiveTenantId]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
